import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  ArrowLeft,
  Edit,
  Trash2,
  Clock,
  Paperclip,
  Link2,
  FileCode,
  Calendar,
  User,
  Users,
  FolderOpen,
  Target,
  Flag,
  Layers,
  Activity,
  CheckCircle,
  AlertCircle,
  Timer,
  GitBranch,
  Info,
  Pencil,
  X,
  ChevronDown,
} from 'lucide-react';
import { t } from '@/i18n/ro';
import { useAuth } from '@/context/AuthContext';
import { useMetadata } from '@/context/MetadataContext';
import { getProjectMembers } from '@/services/projects.service';
import {
  getWorkPackage,
  deleteWorkPackage,
  updateWorkPackage,
  getWorkPackageRelations,
  getWorkPackageChildren,
} from '@/services/workpackages.service';
import { getWorkPackageAttachments } from '@/services/attachments.service';
import { getWorkPackageTimeEntries, calculateTotalTime } from '@/services/timeentries.service';
import { extractIdFromLink } from '@/services/api';
import {
  Button,
  Card,
  Tabs,
  TabPanel,
  LoadingState,
  StatusBadge,
  TypeBadge,
  PriorityBadge,
  ConfirmModal,
  DateInput,
} from '@/components/common';
import { AttachmentsList } from '@/components/attachments/AttachmentsList';
import { WorkPackageFilesTable } from './WorkPackageFilesTable';
import { TimeEntriesList } from './TimeEntriesList';
import { RelationsList } from './RelationsList';
import type { WorkPackage, Attachment, TimeEntry, Relation } from '@/types';
import { format, parseISO, differenceInDays, isPast, isToday } from 'date-fns';
import { ro } from 'date-fns/locale';

// Tab-uri valide pentru work package
const VALID_TABS = ['essential', 'files', 'tracking'];

// Formatare dată în format DD/MM/YYYY
function formatDate(dateStr?: string | null): string {
  if (!dateStr) return '-';
  try {
    return format(parseISO(dateStr), 'dd/MM/yyyy');
  } catch {
    return dateStr;
  }
}

// Formatare dată și oră în format DD/MM/YYYY HH:mm
function formatDateTime(dateStr?: string | null): string {
  if (!dateStr) return '-';
  try {
    return format(parseISO(dateStr), 'dd/MM/yyyy, HH:mm');
  } catch {
    return dateStr;
  }
}

// Calculează zilele rămase
function getDaysRemaining(dueDate?: string | null): { days: number; status: 'overdue' | 'today' | 'soon' | 'normal' } | null {
  if (!dueDate) return null;
  try {
    const due = parseISO(dueDate);
    const days = differenceInDays(due, new Date());

    if (isPast(due) && !isToday(due)) return { days: Math.abs(days), status: 'overdue' };
    if (isToday(due)) return { days: 0, status: 'today' };
    if (days <= 7) return { days, status: 'soon' };
    return { days, status: 'normal' };
  } catch {
    return null;
  }
}

// Parsează durata ISO în ore
function parseISODuration(duration?: string | number | null): number {
  if (!duration) return 0;
  if (typeof duration === 'number') return duration;

  const match = String(duration).match(/^PT(?:(\d+)H)?(?:(\d+)M)?$/i);
  if (match) {
    const hours = parseInt(match[1] || '0');
    const minutes = parseInt(match[2] || '0');
    return hours + minutes / 60;
  }
  return parseFloat(String(duration)) || 0;
}

// Formatează orele
function formatHours(hours: number): string {
  if (hours === 0) return '-';
  const h = Math.floor(hours);
  const m = Math.round((hours - h) * 60);
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
}

export function WorkPackageDetailPage() {
  const { workPackageId } = useParams<{ workPackageId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { getTypeById, getStatusById, getPriorityById, statuses, priorities } = useMetadata();

  const [workPackage, setWorkPackage] = useState<WorkPackage | null>(null);
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [timeEntries, setTimeEntries] = useState<TimeEntry[]>([]);
  const [relations, setRelations] = useState<Relation[]>([]);
  const [children, setChildren] = useState<WorkPackage[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Modal states
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showPriorityModal, setShowPriorityModal] = useState(false);
  const [showDueDateModal, setShowDueDateModal] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Permission states
  const [isProjectAdmin, setIsProjectAdmin] = useState(false);
  const [isProjectMember, setIsProjectMember] = useState(false);
  const [isAssignee, setIsAssignee] = useState(false);
  const [isResponsible, setIsResponsible] = useState(false);

  // Citește tab-ul din hash sau folosește default
  const getTabFromHash = () => {
    const hash = location.hash.replace('#', '');
    return VALID_TABS.includes(hash) ? hash : 'essential';
  };

  const [activeTab, setActiveTab] = useState(getTabFromHash);

  // Sincronizează tab-ul cu hash-ul din URL
  useEffect(() => {
    const hash = location.hash.replace('#', '');
    if (VALID_TABS.includes(hash) && hash !== activeTab) {
      setActiveTab(hash);
    }
  }, [location.hash]);

  // Handler pentru schimbarea tab-ului
  const handleTabChange = (tabId: string) => {
    setActiveTab(tabId);
    navigate(`/work-packages/${workPackageId}#${tabId}`, { replace: true });
  };

  // Încărcare date
  const loadData = async () => {
    if (!workPackageId) return;

    setIsLoading(true);
    setError(null);

    try {
      const wpData = await getWorkPackage(Number(workPackageId));
      setWorkPackage(wpData);

      // Încărcăm date adiționale în paralel
      const [attachmentsData, timeEntriesData, relationsData, childrenData] =
        await Promise.allSettled([
          getWorkPackageAttachments(Number(workPackageId)),
          getWorkPackageTimeEntries(Number(workPackageId)),
          getWorkPackageRelations(Number(workPackageId)),
          getWorkPackageChildren(Number(workPackageId)),
        ]);

      setAttachments(
        attachmentsData.status === 'fulfilled' ? attachmentsData.value : []
      );
      setTimeEntries(
        timeEntriesData.status === 'fulfilled' ? timeEntriesData.value : []
      );
      setRelations(
        relationsData.status === 'fulfilled' ? relationsData.value : []
      );
      setChildren(
        childrenData.status === 'fulfilled' ? childrenData.value : []
      );

      // Check permissions - simplified approach
      // If user can view this page, they are a project member
      setIsProjectMember(true);

      // Check if current user is assignee or responsible
      if (user) {
        const assigneeId = extractIdFromLink(wpData._links.assignee);
        const responsibleId = extractIdFromLink(wpData._links.responsible);

        console.log('Permission check - user.id:', user.id, 'type:', typeof user.id);
        console.log('Permission check - assigneeId:', assigneeId, 'type:', typeof assigneeId);
        console.log('Permission check - responsibleId:', responsibleId, 'type:', typeof responsibleId);
        console.log('Permission check - assignee link:', wpData._links.assignee);
        console.log('Permission check - responsible link:', wpData._links.responsible);

        // Comparăm cu conversie la număr pentru siguranță
        const userId = Number(user.id);
        setIsAssignee(assigneeId === userId);
        setIsResponsible(responsibleId === userId);

        console.log('Permission check - isAssignee:', assigneeId === userId);
        console.log('Permission check - isResponsible:', responsibleId === userId);
      }

      // Check if user is admin (global admin)
      if (user?.admin) {
        setIsProjectAdmin(true);
      } else {
        // Try to check project membership for admin role
        const projectId = extractIdFromLink(wpData._links.project);
        if (projectId && user) {
          try {
            const members = await getProjectMembers(projectId);
            console.log('Project members loaded:', members);
            console.log('Looking for user.id:', user.id);

            // Find current user in members list
            const currentUserMember = members.find(m => {
              // Check by user ID in embedded principal (from auth-server endpoint)
              const embeddedPrincipalId = m._embedded?.principal?.id;
              // Also check by user ID in principal link (HAL format)
              const principalHref = m._links?.principal?.href || '';
              const linkPrincipalId = principalHref.match(/\/users\/(\d+)/)?.[1];

              const isMatch = embeddedPrincipalId === user.id ||
                              embeddedPrincipalId === Number(user.id) ||
                              linkPrincipalId === String(user.id);

              console.log('Member check:', {
                memberId: m.id,
                embeddedPrincipalId,
                linkPrincipalId,
                userId: user.id,
                isMatch
              });

              return isMatch;
            });

            if (currentUserMember) {
              console.log('Found current user member:', currentUserMember);

              // Check if user has Manager/Admin role
              // Check embedded roles first (has full role objects with name)
              const embeddedRoles = currentUserMember._embedded?.roles || [];
              // Also check _links.roles for HAL links with title
              const linkRoles = currentUserMember._links?.roles || [];

              const isAdminFromEmbedded = embeddedRoles.some(role => {
                const roleName = (role.name || '').toLowerCase();
                return roleName.includes('manager') || roleName.includes('admin') || roleName.includes('coordonator');
              });

              const isAdminFromLinks = linkRoles.some((role: { href?: string; title?: string }) => {
                const roleName = (role.title || '').toLowerCase();
                return roleName.includes('manager') || roleName.includes('admin') || roleName.includes('coordonator');
              });

              console.log('Role check - embeddedRoles:', embeddedRoles);
              console.log('Role check - linkRoles:', linkRoles);
              console.log('Role check - isAdminFromEmbedded:', isAdminFromEmbedded);
              console.log('Role check - isAdminFromLinks:', isAdminFromLinks);

              setIsProjectAdmin(isAdminFromEmbedded || isAdminFromLinks);
            } else {
              console.log('Current user NOT found in project members list!');
              console.log('User ID being searched:', user.id, typeof user.id);
            }
          } catch (err) {
            console.log('Could not check project membership:', err);
            // Default: not admin unless global admin
            setIsProjectAdmin(false);
          }
        }
      }
    } catch (err) {
      console.error('Failed to load work package:', err);
      setError(t('errors.loadFailed'));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [workPackageId, user]);

  // Debug permissions
  console.log('Permission debug:', {
    user: user?.id,
    userAdmin: user?.admin,
    isProjectAdmin,
    isProjectMember,
    isAssignee,
    isResponsible,
    workPackageId: workPackage?.id,
    assignee: workPackage ? extractIdFromLink(workPackage._links.assignee) : null,
    responsible: workPackage ? extractIdFromLink(workPackage._links.responsible) : null,
  });

  // Check if current user can edit specific fields
  // Adminii pot edita tot
  // Responsabilul poate edita: Status, Prioritate și Progres
  // Executantul poate edita doar Progresul

  const canEditStatus = () => {
    return isProjectAdmin || isResponsible;
  };

  const canEditPriority = () => {
    return isProjectAdmin || isResponsible;
  };

  const canEditDueDate = () => {
    // Doar adminii pot modifica termenul
    return isProjectAdmin;
  };

  // Doar adminii pot accesa pagina completă de editare
  const canFullEdit = () => {
    return isProjectAdmin;
  };

  // Update handlers
  const handleUpdateStatus = async (newStatusId: number) => {
    if (!workPackage) return;
    setIsSaving(true);
    try {
      await updateWorkPackage(workPackage.id, workPackage.lockVersion, { statusId: newStatusId });
      await loadData();
      setShowStatusModal(false);
    } catch (err) {
      console.error('Failed to update status:', err);
      setError('Eroare la actualizarea statusului');
    } finally {
      setIsSaving(false);
    }
  };

  const handleUpdatePriority = async (newPriorityId: number) => {
    if (!workPackage) return;
    setIsSaving(true);
    try {
      await updateWorkPackage(workPackage.id, workPackage.lockVersion, { priorityId: newPriorityId });
      await loadData();
      setShowPriorityModal(false);
    } catch (err) {
      console.error('Failed to update priority:', err);
      setError('Eroare la actualizarea priorității');
    } finally {
      setIsSaving(false);
    }
  };

  const handleUpdateDueDate = async (newDueDate: string) => {
    if (!workPackage) return;
    setIsSaving(true);
    try {
      await updateWorkPackage(workPackage.id, workPackage.lockVersion, { dueDate: newDueDate || undefined });
      await loadData();
      setShowDueDateModal(false);
    } catch (err) {
      console.error('Failed to update due date:', err);
      setError('Eroare la actualizarea termenului');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!workPackage) return;

    setIsDeleting(true);
    try {
      await deleteWorkPackage(workPackage.id);
      navigate(-1);
    } catch (err) {
      console.error('Failed to delete work package:', err);
      setError(t('errors.deleteFailed'));
    } finally {
      setIsDeleting(false);
      setShowDeleteModal(false);
    }
  };

  if (isLoading) {
    return <LoadingState message={t('common.loading')} />;
  }

  if (error || !workPackage) {
    return (
      <div className="p-4 bg-danger-50 border border-danger-200 rounded-lg text-danger-700">
        {error || t('errors.notFound')}
      </div>
    );
  }

  const typeId = extractIdFromLink(workPackage._links.type);
  const statusId = extractIdFromLink(workPackage._links.status);
  const priorityId = extractIdFromLink(workPackage._links.priority);

  const type = typeId ? getTypeById(typeId) : null;
  const status = statusId ? getStatusById(statusId) : null;
  const priority = priorityId ? getPriorityById(priorityId) : null;

  const projectName = workPackage._links.project?.title || '';
  const assigneeName = workPackage._links.assignee?.title || '';
  const responsibleName = workPackage._links.responsible?.title || '';
  const parentName = workPackage._links.parent?.title || '';
  const versionName = workPackage._links.version?.title || '';
  const categoryName = workPackage._links.category?.title || '';
  const authorName = workPackage._links.author?.title || '';

  const totalTime = calculateTotalTime(timeEntries);
  const daysRemaining = getDaysRemaining(workPackage.dueDate);
  const estimatedHours = parseISODuration(workPackage.estimatedTime);
  const spentHours = totalTime.hours;
  const remainingHours = parseISODuration(workPackage.remainingTime);

  // Determină tipul pentru iconă și culoare
  const getTypeIcon = () => {
    const typeName = type?.name?.toLowerCase() || '';
    if (type?.isMilestone || typeName.includes('reper') || typeName.includes('milestone')) {
      return <Flag className="w-5 h-5" />;
    }
    if (typeName.includes('fază') || typeName.includes('faza') || typeName.includes('phase')) {
      return <Layers className="w-5 h-5" />;
    }
    return <Activity className="w-5 h-5" />;
  };

  const getTypeColor = () => {
    const typeName = type?.name?.toLowerCase() || '';
    if (type?.isMilestone || typeName.includes('reper') || typeName.includes('milestone')) {
      return 'bg-purple-100 text-purple-700 border-purple-200';
    }
    if (typeName.includes('fază') || typeName.includes('faza') || typeName.includes('phase')) {
      return 'bg-blue-100 text-blue-700 border-blue-200';
    }
    return 'bg-green-100 text-green-700 border-green-200';
  };

  const tabs = [
    {
      id: 'essential',
      label: t('workPackages.tabs.essential'),
    },
    {
      id: 'files',
      label: 'Fișiere',
      badge: attachments.length,
    },
    {
      id: 'tracking',
      label: t('workPackages.tabs.tracking'),
      badge: timeEntries.length,
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate(-1)}
          leftIcon={<ArrowLeft className="w-4 h-4" />}
        >
          {t('common.back')}
        </Button>
      </div>

      {/* Header Section */}
      <div className="bg-white rounded-lg border border-gray-200 p-5 shadow-sm">
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
          <div className="flex-1">
            {/* Badges */}
            <div className="flex flex-wrap items-center gap-2 mb-3">
              <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-sm font-medium border ${getTypeColor()}`}>
                {getTypeIcon()}
                <span>{type?.name || 'Element'}</span>
              </div>
              {status && (
                <StatusBadge
                  name={status.name}
                  color={status.color}
                  isClosed={status.isClosed}
                />
              )}
              {priority && (
                <PriorityBadge name={priority.name} color={priority.color} />
              )}
            </div>

            {/* Titlu */}
            <h1 className="text-xl lg:text-2xl font-bold text-gray-900 mb-2">
              {workPackage.subject}
            </h1>

            {/* Meta */}
            {/* Meta - project link */}
            <div className="flex flex-wrap items-center gap-3 text-gray-500 text-sm">
              <button
                onClick={() => {
                  const projectId = extractIdFromLink(workPackage._links.project);
                  if (projectId) navigate(`/projects/${projectId}`);
                }}
                className="flex items-center gap-1 hover:text-primary-600 transition-colors"
              >
                <FolderOpen className="w-4 h-4" />
                {projectName}
              </button>
              {authorName && (
                <span className="flex items-center gap-1">
                  <User className="w-4 h-4" />
                  Creat de {authorName}
                </span>
              )}
            </div>
          </div>

          {/* Acțiuni rapide */}
          <div className="flex flex-wrap gap-2">
            <Button
              variant="secondary"
              size="sm"
              leftIcon={<Paperclip className="w-4 h-4" />}
              onClick={() => handleTabChange('files')}
            >
              Fișiere ({attachments.length})
            </Button>
            <Button
              variant="secondary"
              size="sm"
              leftIcon={<Clock className="w-4 h-4" />}
              onClick={() => handleTabChange('tracking')}
            >
              Timpi ({timeEntries.length})
            </Button>
            {canFullEdit() && (
              <>
                <Button
                  variant="primary"
                  size="sm"
                  leftIcon={<Edit className="w-4 h-4" />}
                  onClick={() => navigate(`/work-packages/${workPackage.id}/edit`)}
                >
                  {t('common.edit')}
                </Button>
                {workPackage._links.delete && (
                  <Button
                    variant="danger"
                    size="sm"
                    leftIcon={<Trash2 className="w-4 h-4" />}
                    onClick={() => setShowDeleteModal(true)}
                  >
                    {t('common.delete')}
                  </Button>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Status */}
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${status?.isClosed ? 'bg-green-100' : 'bg-blue-100'}`}>
                {status?.isClosed ? (
                  <CheckCircle className={`w-5 h-5 ${status?.isClosed ? 'text-green-600' : 'text-blue-600'}`} />
                ) : (
                  <Activity className="w-5 h-5 text-blue-600" />
                )}
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wide">Status</p>
                <p className="font-semibold text-gray-900">{status?.name || '-'}</p>
              </div>
            </div>
            {canEditStatus() && (
              <button
                onClick={() => setShowStatusModal(true)}
                className="p-1.5 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-500 hover:text-primary-600 transition-colors"
                title="Editează status"
              >
                <Pencil className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>

        {/* Prioritate */}
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-orange-100">
                <AlertCircle className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wide">Prioritate</p>
                <p className="font-semibold text-gray-900">{priority?.name || '-'}</p>
              </div>
            </div>
            {canEditPriority() && (
              <button
                onClick={() => setShowPriorityModal(true)}
                className="p-1.5 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-500 hover:text-primary-600 transition-colors"
                title="Editează prioritate"
              >
                <Pencil className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>

        {/* Data limită */}
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${
                daysRemaining?.status === 'overdue' ? 'bg-red-100' :
                daysRemaining?.status === 'today' ? 'bg-yellow-100' :
                daysRemaining?.status === 'soon' ? 'bg-orange-100' : 'bg-gray-100'
              }`}>
                <Calendar className={`w-5 h-5 ${
                  daysRemaining?.status === 'overdue' ? 'text-red-600' :
                  daysRemaining?.status === 'today' ? 'text-yellow-600' :
                  daysRemaining?.status === 'soon' ? 'text-orange-600' : 'text-gray-600'
                }`} />
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wide">Data limită</p>
                <p className="font-semibold text-gray-900">
                  {workPackage.dueDate ? formatDate(workPackage.dueDate) : '-'}
                </p>
                {daysRemaining && (
                  <p className={`text-xs ${
                    daysRemaining.status === 'overdue' ? 'text-red-600' :
                    daysRemaining.status === 'today' ? 'text-yellow-600' :
                    daysRemaining.status === 'soon' ? 'text-orange-600' : 'text-gray-500'
                  }`}>
                    {daysRemaining.status === 'overdue' && `Întârziat cu ${daysRemaining.days} zile`}
                    {daysRemaining.status === 'today' && 'Scadent astăzi'}
                    {daysRemaining.status === 'soon' && `Mai sunt ${daysRemaining.days} zile`}
                    {daysRemaining.status === 'normal' && `Mai sunt ${daysRemaining.days} zile`}
                  </p>
                )}
              </div>
            </div>
            {canEditDueDate() && (
              <button
                onClick={() => setShowDueDateModal(true)}
                className="p-1.5 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-500 hover:text-primary-600 transition-colors"
                title="Editează termen"
              >
                <Pencil className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>

      </div>

      {/* Tabs */}
      <Card padding="none">
        <div className="px-4 pt-4">
          <Tabs tabs={tabs} activeTab={activeTab} onChange={handleTabChange} />
        </div>

        <div className="p-6">
          {/* Tab: Esențial */}
          <TabPanel tabId="essential" activeTab={activeTab}>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Coloana principală - Informații */}
              <div className="lg:col-span-2 space-y-6">
                {/* Descriere / Note - always show, with placeholder if empty */}
                <div className="bg-gray-50 rounded-xl p-5">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <FileCode className="w-5 h-5 text-primary-500" />
                    Descriere / Note
                  </h3>
                  {workPackage.description?.html ? (
                    <div
                      className="prose prose-sm max-w-none text-gray-700 bg-white p-4 rounded-lg border border-gray-200"
                      dangerouslySetInnerHTML={{
                        __html: workPackage.description.html,
                      }}
                    />
                  ) : (
                    <div className="bg-white p-4 rounded-lg border border-gray-200 text-gray-400 italic text-sm">
                      Nu există descriere pentru această activitate.
                    </div>
                  )}
                </div>

                {/* Card Informații generale */}
                <div className="bg-gray-50 rounded-xl p-5">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <Info className="w-5 h-5 text-primary-500" />
                    Informații generale
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <InfoItem
                      icon={<Target className="w-4 h-4" />}
                      label="Tip"
                      value={type?.name || '-'}
                      highlight
                    />
                    <InfoItem
                      icon={<FolderOpen className="w-4 h-4" />}
                      label="Proiect"
                      value={projectName || '-'}
                    />
                    {categoryName && (
                      <InfoItem
                        icon={<Layers className="w-4 h-4" />}
                        label="Categorie"
                        value={categoryName}
                      />
                    )}
                    {versionName && (
                      <InfoItem
                        icon={<GitBranch className="w-4 h-4" />}
                        label="Versiune"
                        value={versionName}
                      />
                    )}
                  </div>
                </div>

                {/* Card Persoane - compact layout */}
                <div className="bg-gray-50 rounded-xl p-5">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <Users className="w-5 h-5 text-primary-500" />
                    Persoane implicate
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <PersonItem
                      role="Executant"
                      name={assigneeName || 'Neatribuit'}
                      isEmpty={!assigneeName}
                    />
                    <PersonItem
                      role="Responsabil"
                      name={responsibleName || 'Neatribuit'}
                      isEmpty={!responsibleName}
                    />
                    {authorName && (
                      <PersonItem
                        role="Autor"
                        name={authorName}
                        isEmpty={false}
                      />
                    )}
                    {parentName && (
                      <div
                        className="flex items-center gap-3 p-3 bg-white rounded-lg border border-gray-200 cursor-pointer hover:border-primary-300 transition-colors"
                        onClick={() => {
                          const parentId = extractIdFromLink(workPackage._links.parent);
                          if (parentId) navigate(`/work-packages/${parentId}`);
                        }}
                      >
                        <div className="p-2 bg-purple-100 rounded-lg">
                          <GitBranch className="w-4 h-4 text-purple-600" />
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Element părinte</p>
                          <p className="font-medium text-gray-900">{parentName}</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Coloana laterală - Timeline și statistici */}
              <div className="space-y-6">
                {/* Card Date */}
                <div className="bg-gradient-to-br from-primary-50 to-primary-100 rounded-xl p-5 border border-primary-200">
                  <h3 className="text-lg font-semibold text-primary-900 mb-4 flex items-center gap-2">
                    <Calendar className="w-5 h-5 text-primary-600" />
                    Perioada
                  </h3>
                  <div className="space-y-4">
                    <div className="bg-white/80 rounded-lg p-3">
                      <p className="text-xs text-gray-500 uppercase tracking-wide">Data început</p>
                      <p className="font-semibold text-gray-900">
                        {formatDate(workPackage.startDate)}
                      </p>
                    </div>
                    <div className="flex items-center justify-center">
                      <div className="w-0.5 h-6 bg-primary-300" />
                    </div>
                    <div className={`rounded-lg p-3 ${
                      daysRemaining?.status === 'overdue' ? 'bg-red-100' :
                      daysRemaining?.status === 'today' ? 'bg-yellow-100' : 'bg-white/80'
                    }`}>
                      <p className="text-xs text-gray-500 uppercase tracking-wide">Data limită</p>
                      <p className="font-semibold text-gray-900">
                        {formatDate(workPackage.dueDate)}
                      </p>
                      {daysRemaining && (
                        <p className={`text-xs mt-1 ${
                          daysRemaining.status === 'overdue' ? 'text-red-600 font-medium' :
                          daysRemaining.status === 'today' ? 'text-yellow-700 font-medium' : 'text-gray-500'
                        }`}>
                          {daysRemaining.status === 'overdue' && `⚠ Întârziat cu ${daysRemaining.days} zile`}
                          {daysRemaining.status === 'today' && '⏰ Scadent astăzi!'}
                          {daysRemaining.status === 'soon' && `📅 Mai sunt ${daysRemaining.days} zile`}
                          {daysRemaining.status === 'normal' && `📅 Mai sunt ${daysRemaining.days} zile`}
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Card Timp */}
                <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl p-5 border border-orange-200">
                  <h3 className="text-lg font-semibold text-orange-900 mb-4 flex items-center gap-2">
                    <Timer className="w-5 h-5 text-orange-600" />
                    Timp de lucru
                  </h3>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center bg-white/80 rounded-lg p-3">
                      <span className="text-sm text-gray-600">Estimat</span>
                      <span className="font-semibold text-gray-900">{formatHours(estimatedHours)}</span>
                    </div>
                    <div className="flex justify-between items-center bg-white/80 rounded-lg p-3">
                      <span className="text-sm text-gray-600">Înregistrat</span>
                      <span className="font-semibold text-primary-600">{formatHours(spentHours)}</span>
                    </div>
                    <div className="flex justify-between items-center bg-white/80 rounded-lg p-3">
                      <span className="text-sm text-gray-600">Rămas</span>
                      <span className="font-semibold text-gray-900">{formatHours(remainingHours)}</span>
                    </div>
                    {estimatedHours > 0 && (
                      <div className="pt-3 space-y-3">
                        {/* Timp înregistrat vs estimat */}
                        <div>
                          <div className="flex justify-between text-xs text-gray-500 mb-1">
                            <span className="flex items-center gap-1">
                              <span className="w-2 h-2 rounded-full bg-blue-500"></span>
                              Timp înregistrat
                            </span>
                            <span>{formatHours(spentHours)} / {formatHours(estimatedHours)} ({Math.round((spentHours / estimatedHours) * 100)}%)</span>
                          </div>
                          <div className="h-2.5 bg-white/80 rounded-full overflow-hidden">
                            <div
                              className={`h-full rounded-full ${
                                spentHours > estimatedHours ? 'bg-red-500' :
                                spentHours > estimatedHours * 0.8 ? 'bg-orange-500' : 'bg-blue-500'
                              }`}
                              style={{ width: `${Math.min((spentHours / estimatedHours) * 100, 100)}%` }}
                            />
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Statistici rapide */}
                <div className="bg-white rounded-xl p-5 border border-gray-200">
                  <h3 className="text-sm font-semibold text-gray-900 mb-3">Statistici</h3>
                  <div className="space-y-2">
                    <StatItem icon={<Paperclip className="w-4 h-4" />} label="Atașamente" value={attachments.length} />
                    <StatItem icon={<Clock className="w-4 h-4" />} label="Înregistrări timp" value={timeEntries.length} />
                  </div>
                </div>
              </div>
            </div>

            {/* Detalii tehnice - collapsible */}
            <TechnicalDetails
              id={workPackage.id}
              createdAt={workPackage.createdAt}
              updatedAt={workPackage.updatedAt}
            />
          </TabPanel>

          {/* Tab: Fișiere */}
          <TabPanel tabId="files" activeTab={activeTab}>
            <WorkPackageFilesTable
              attachments={attachments}
              workPackageId={workPackage.id}
              currentUserId={user?.id}
              onFileDeleted={loadData}
              canUpload={isProjectMember}
            />
          </TabPanel>

          {/* Tab: Urmărire */}
          <TabPanel tabId="tracking" activeTab={activeTab}>
            <div className="mb-6">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-gray-900">
                  {t('timeTracking.title')}
                </h3>
                <div className="text-right">
                  <p className="text-sm text-gray-500">{t('timeTracking.total')}</p>
                  <p className="text-xl font-bold text-gray-900">
                    {totalTime.formatted || '0h'}
                  </p>
                </div>
              </div>
            </div>

            <TimeEntriesList
              timeEntries={timeEntries}
              workPackageId={workPackage.id}
              projectId={extractIdFromLink(workPackage._links.project) || 0}
              onRefresh={loadData}
              canAddTime={isProjectMember}
            />
          </TabPanel>
        </div>
      </Card>

      {/* Modal confirmare ștergere */}
      <ConfirmModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDelete}
        title={t('workPackages.deleteWorkPackage')}
        message={t('workPackages.confirmDelete')}
        confirmLabel={t('common.delete')}
        cancelLabel={t('common.cancel')}
        variant="danger"
        isLoading={isDeleting}
      />

      {/* Modal editare Status */}
      <EditStatusModal
        isOpen={showStatusModal}
        onClose={() => setShowStatusModal(false)}
        onSave={handleUpdateStatus}
        currentStatusId={statusId || 0}
        statuses={statuses}
        isSaving={isSaving}
      />

      {/* Modal editare Prioritate */}
      <EditPriorityModal
        isOpen={showPriorityModal}
        onClose={() => setShowPriorityModal(false)}
        onSave={handleUpdatePriority}
        currentPriorityId={priorityId || 0}
        priorities={priorities}
        isSaving={isSaving}
      />

      {/* Modal editare Data limită */}
      <EditDueDateModal
        isOpen={showDueDateModal}
        onClose={() => setShowDueDateModal(false)}
        onSave={handleUpdateDueDate}
        currentDueDate={workPackage.dueDate || ''}
        isSaving={isSaving}
      />

    </div>
  );
}

// Componente auxiliare

interface FieldRowProps {
  label: string;
  value: string;
}

function FieldRow({ label, value }: FieldRowProps) {
  return (
    <div>
      <dt className="text-sm font-medium text-gray-500">{label}</dt>
      <dd className="mt-1 text-gray-900">{value}</dd>
    </div>
  );
}

interface InfoItemProps {
  icon: React.ReactNode;
  label: string;
  value: string;
  highlight?: boolean;
  badge?: string;
  badgeColor?: 'green' | 'blue' | 'orange' | 'red';
}

function InfoItem({ icon, label, value, highlight, badge, badgeColor = 'blue' }: InfoItemProps) {
  const badgeColors = {
    green: 'bg-green-100 text-green-700',
    blue: 'bg-blue-100 text-blue-700',
    orange: 'bg-orange-100 text-orange-700',
    red: 'bg-red-100 text-red-700',
  };

  return (
    <div className={`flex items-start gap-3 p-3 rounded-lg ${highlight ? 'bg-primary-50 border border-primary-100' : 'bg-white border border-gray-200'}`}>
      <div className={`p-1.5 rounded ${highlight ? 'bg-primary-100 text-primary-600' : 'bg-gray-100 text-gray-500'}`}>
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-xs text-gray-500">{label}</p>
        <div className="flex items-center gap-2">
          <p className="font-medium text-gray-900 truncate">{value}</p>
          {badge && (
            <span className={`text-xs px-1.5 py-0.5 rounded-full ${badgeColors[badgeColor]}`}>
              {badge}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

interface PersonItemProps {
  role: string;
  name: string;
  isEmpty?: boolean;
}

function PersonItem({ role, name, isEmpty }: PersonItemProps) {
  return (
    <div className={`flex items-center gap-3 p-3 rounded-lg border ${isEmpty ? 'bg-gray-50 border-gray-200' : 'bg-white border-gray-200'}`}>
      <div className={`p-2 rounded-full ${isEmpty ? 'bg-gray-200' : 'bg-primary-100'}`}>
        <User className={`w-4 h-4 ${isEmpty ? 'text-gray-400' : 'text-primary-600'}`} />
      </div>
      <div>
        <p className="text-xs text-gray-500">{role}</p>
        <p className={`font-medium ${isEmpty ? 'text-gray-400 italic' : 'text-gray-900'}`}>{name}</p>
      </div>
    </div>
  );
}

interface StatItemProps {
  icon: React.ReactNode;
  label: string;
  value: number;
}

function StatItem({ icon, label, value }: StatItemProps) {
  return (
    <div className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
      <div className="flex items-center gap-2 text-gray-600">
        {icon}
        <span className="text-sm">{label}</span>
      </div>
      <span className={`font-semibold ${value > 0 ? 'text-primary-600' : 'text-gray-400'}`}>{value}</span>
    </div>
  );
}

// Collapsible Technical Details component
interface TechnicalDetailsProps {
  id: number;
  createdAt?: string;
  updatedAt?: string;
}

function TechnicalDetails({ id, createdAt, updatedAt }: TechnicalDetailsProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div className="mt-6 border-t border-gray-200 pt-4">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 transition-colors"
      >
        <Info className="w-4 h-4" />
        <span>Detalii tehnice</span>
        <ChevronDown className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
      </button>
      {isExpanded && (
        <div className="mt-3 p-4 bg-gray-50 rounded-lg text-sm text-gray-600 space-y-2">
          <div className="flex justify-between">
            <span>ID intern:</span>
            <span className="font-mono text-gray-900">#{id}</span>
          </div>
          {createdAt && (
            <div className="flex justify-between">
              <span>Creat la:</span>
              <span className="text-gray-900">{formatDateTime(createdAt)}</span>
            </div>
          )}
          {updatedAt && (
            <div className="flex justify-between">
              <span>Ultima actualizare:</span>
              <span className="text-gray-900">{formatDateTime(updatedAt)}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Modal Components for inline editing

interface EditStatusModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (statusId: number) => void;
  currentStatusId: number;
  statuses: { id: number; name: string; color?: string; isClosed?: boolean }[];
  isSaving: boolean;
}

function EditStatusModal({ isOpen, onClose, onSave, currentStatusId, statuses, isSaving }: EditStatusModalProps) {
  const [selectedStatus, setSelectedStatus] = useState(currentStatusId);

  useEffect(() => {
    setSelectedStatus(currentStatusId);
  }, [currentStatusId, isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="fixed inset-0 bg-black/50" onClick={onClose} />
        <div className="relative bg-white rounded-xl shadow-xl w-full max-w-md p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Schimbă Status</h3>
            <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg">
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          <div className="space-y-2 max-h-80 overflow-y-auto">
            {statuses.map((status) => (
              <button
                key={status.id}
                onClick={() => setSelectedStatus(status.id)}
                className={`w-full flex items-center gap-3 p-3 rounded-lg border transition-colors ${
                  selectedStatus === status.id
                    ? 'border-primary-500 bg-primary-50'
                    : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                }`}
              >
                <div
                  className="w-3 h-3 rounded-full"
                  style={{ backgroundColor: status.color || '#6B7280' }}
                />
                <span className="font-medium text-gray-900">{status.name}</span>
                {status.isClosed && (
                  <span className="ml-auto text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">
                    Închis
                  </span>
                )}
              </button>
            ))}
          </div>

          <div className="flex justify-end gap-3 mt-6">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              disabled={isSaving}
            >
              Anulează
            </button>
            <button
              onClick={() => onSave(selectedStatus)}
              disabled={isSaving || selectedStatus === currentStatusId}
              className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSaving ? 'Se salvează...' : 'Salvează'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

interface EditPriorityModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (priorityId: number) => void;
  currentPriorityId: number;
  priorities: { id: number; name: string; color?: string }[];
  isSaving: boolean;
}

function EditPriorityModal({ isOpen, onClose, onSave, currentPriorityId, priorities, isSaving }: EditPriorityModalProps) {
  const [selectedPriority, setSelectedPriority] = useState(currentPriorityId);

  useEffect(() => {
    setSelectedPriority(currentPriorityId);
  }, [currentPriorityId, isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="fixed inset-0 bg-black/50" onClick={onClose} />
        <div className="relative bg-white rounded-xl shadow-xl w-full max-w-md p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Schimbă Prioritate</h3>
            <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg">
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          <div className="space-y-2 max-h-80 overflow-y-auto">
            {priorities.map((priority) => (
              <button
                key={priority.id}
                onClick={() => setSelectedPriority(priority.id)}
                className={`w-full flex items-center gap-3 p-3 rounded-lg border transition-colors ${
                  selectedPriority === priority.id
                    ? 'border-primary-500 bg-primary-50'
                    : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                }`}
              >
                <div
                  className="w-3 h-3 rounded-full"
                  style={{ backgroundColor: priority.color || '#6B7280' }}
                />
                <span className="font-medium text-gray-900">{priority.name}</span>
              </button>
            ))}
          </div>

          <div className="flex justify-end gap-3 mt-6">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              disabled={isSaving}
            >
              Anulează
            </button>
            <button
              onClick={() => onSave(selectedPriority)}
              disabled={isSaving || selectedPriority === currentPriorityId}
              className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSaving ? 'Se salvează...' : 'Salvează'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

interface EditDueDateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (dueDate: string) => void;
  currentDueDate: string;
  isSaving: boolean;
}

function EditDueDateModal({ isOpen, onClose, onSave, currentDueDate, isSaving }: EditDueDateModalProps) {
  const [dueDate, setDueDate] = useState(currentDueDate?.split('T')[0] || '');

  useEffect(() => {
    setDueDate(currentDueDate?.split('T')[0] || '');
  }, [currentDueDate, isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="fixed inset-0 bg-black/50" onClick={onClose} />
        <div className="relative bg-white rounded-xl shadow-xl w-full max-w-md p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Schimbă Data limită</h3>
            <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg">
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          <div className="mb-6">
            <DateInput
              label="Data limită"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
            />
          </div>

          <div className="flex justify-end gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              disabled={isSaving}
            >
              Anulează
            </button>
            <button
              onClick={() => onSave(dueDate)}
              disabled={isSaving}
              className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSaving ? 'Se salvează...' : 'Salvează'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

